/* toast.js
   Sistema de notificaciones propio (sin dependencias).
   - Es un singleton: se puede importar y llamar desde CUALQUIER archivo
     (componentes, archivos de API, utils, etc.), no solo dentro de React.
   - El render lo hace <ToastContainer/>, que se monta UNA sola vez en el root.

   Uso:
     import { toast } from "../utils/toast/toast";
     toast.error("No se pudo iniciar sesión");
     toast.success("Sesión iniciada");
     toast.show({ type: "warning", title: "Atención", message: "...", duration: 6000 });

     // Modal de confirmación (diálogo centrado). Devuelve Promise<boolean>:
     const ok = await toast.confirm({
       type: "error",
       title: "¿Eliminar este ítem?",
       message: "Esta acción es definitiva.",
       confirmLabel: "Eliminar",
     });
     if (ok) borrarItem();
*/

let idSeq = 0;
const listeners = new Set();
let toasts = [];

// Config global por defecto (personalizable con toast.configure({...}))
const DEFAULTS = {
  type: "info",        // "info" | "success" | "error" | "warning"
  title: null,         // título opcional
  message: "",         // texto principal
  duration: 4000,      // ms; 0 o null = no se cierra solo
  position: "top-right", // top-right | top-left | top-center | bottom-right | bottom-left | bottom-center
  dismissible: true,   // muestra botón de cerrar
  showProgress: true,  // barra de progreso del temporizador
  icon: undefined,     // ReactNode para override; null/false oculta el ícono
  action: null,        // { label, onClick } botón de acción opcional
  className: "",        // clase extra para estilos puntuales
  // --- toast especial (expandible + cuenta regresiva) ---
  expandable: false,   // muestra la flecha para abrir/cerrar el detalle
  details: null,       // texto/contenido que se muestra al expandir
  defaultExpanded: false, // inicia expandido
  countdown: false,    // muestra "se cerrará en N seg. Clic para detener"
  // --- modal de confirmación (diálogo centrado) ---
  variant: null,       // "modal" = diálogo centrado con backdrop; null = toast normal
};

function emit() {
  for (const fn of listeners) fn(toasts);
}

function add(input) {
  const id = ++idSeq;
  const item = { id, ...DEFAULTS, ...input };
  toasts = [...toasts, item];
  emit();
  return id;
}

function dismiss(id) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

function clear() {
  toasts = [];
  emit();
}

/** Suscripción para el contenedor. Devuelve función para desuscribir. */
export function subscribe(listener) {
  listeners.add(listener);
  listener(toasts);
  return () => listeners.delete(listener);
}

/** Normaliza: acepta toast.error("texto") o toast.error({ title, message, ... }) */
function build(type, input, options) {
  const base = typeof input === "string" ? { message: input } : input || {};
  return add({ type, ...base, ...(options || {}) });
}

export const toast = {
  show: (opts) => add(opts || {}),
  info: (input, options) => build("info", input, options),
  success: (input, options) => build("success", input, options),
  error: (input, options) => build("error", input, options),
  warning: (input, options) => build("warning", input, options),
  /**
   * Toast especial: tarjeta expandible con cuenta regresiva (10–15s).
   *   toast.detailed({ type:"warning", title:"...", details:"...",
   *                    action:{ label:"Aceptar", onClick }, duration:12000 })
   */
  detailed: (input, options) => {
    const base = typeof input === "string" ? { title: input } : input || {};
    const merged = { ...base, ...(options || {}) };
    const duration = Math.min(15000, Math.max(10000, merged.duration ?? 12000));
    return add({
      type: "info",
      countdown: true,
      dismissible: true,
      expandable: merged.details != null,
      ...merged,
      duration,
    });
  },
  /**
   * Modal de confirmación centrado (estilo diálogo).
   * Devuelve Promise<boolean>: true si confirma, false si cancela/cierra.
   * Tipos: "success" | "error" | "warning" | "info" (colorea ícono y botón).
   *
   *   const ok = await toast.confirm({
   *     type: "error",
   *     title: "¿Eliminar este ítem?",
   *     message: "Esta acción es definitiva.",
   *     confirmLabel: "Eliminar",
   *   });
   *   if (ok) borrar();
   *
   * Un solo botón (estilo alerta): pasa cancelLabel: null.
   * También acepta callbacks: { onConfirm, onCancel }.
   */
  confirm: (input, options) => {
    const base = typeof input === "string" ? { message: input } : input || {};
    const merged = { ...base, ...(options || {}) };
    return new Promise((resolve) => {
      add({
        variant: "modal",
        type: "info",
        title: null,
        message: "",
        duration: 0,          // los modales NO se cierran solos
        showProgress: false,
        dismissible: true,    // ESC / clic en el fondo = cancelar (usa dismissible:false para bloquear)
        confirmLabel: "Aceptar",
        cancelLabel: "Cancelar",
        ...merged,
        onConfirm: () => { merged.onConfirm?.(); resolve(true); },
        onCancel:  () => { merged.onCancel?.();  resolve(false); },
      });
    });
  },
  dismiss,
  clear,
  /** Cambia los valores por defecto globales (duración, posición, etc.) */
  configure: (cfg) => Object.assign(DEFAULTS, cfg || {}),
};

export default toast;